/* eslint-disable @hakio/no-untranslated-terminology -- flag panel copy is internal (staff-only), not tenant-facing */
import type { ColDef, ICellRendererParams, ValueGetterParams } from "ag-grid-community"

import { Tooltip } from "~/components/reusable/Tooltip"
import {
  useFlagPanel,
  useRequestFlagChange,
} from "~/features/feature-flags/client/FlagPanelProvider"
import {
  FlagSelect,
  FlagSourceLabel,
  keepSelectKeys,
} from "~/features/feature-flags/client/FlagControls"
import { FlagStatusCell } from "~/features/feature-flags/client/FlagStatusBadge"
import { RouteLink } from "~/features/feature-flags/client/primitives"
import { targetsRequest } from "~/features/feature-flags/client/model/changeRequest"
import type { CompanyFlagRow } from "~/features/feature-flags/client/model/flagPage"
import { blockedByReason } from "~/features/feature-flags/client/model/labels"
import { flagName } from "~/features/feature-flags/shared/registry"
import { FLAG_STATE_ORDER, flagSourceText } from "~/features/feature-flags/client/model/status"

function SettingCell({ data }: ICellRendererParams<CompanyFlagRow>) {
  const panel = useFlagPanel()
  const requestChange = useRequestFlagChange()
  if (!data) return null
  const { flagKey, orgId, orgName } = data
  return (
    <FlagSelect
      ariaLabel={`Setting for ${flagName(flagKey)} at ${orgName}`}
      size="small"
      value={data.override}
      defaultValue={data.defaultValue}
      disabledReason={
        panel.lockReason(flagKey, "org") ??
        (data.status.blockedBy && blockedByReason(data.status.blockedBy))
      }
      onChange={(value) => requestChange(targetsRequest("org", flagKey, orgId, value))}
    />
  )
}

function SourceCell({ data }: ICellRendererParams<CompanyFlagRow>) {
  if (!data) return null
  return (
    <span className="flex h-full min-w-0 items-center">
      <FlagSourceLabel {...data.status} />
    </span>
  )
}

function UserOverridesCell({ data }: ICellRendererParams<CompanyFlagRow>) {
  if (!data?.userOverrides) return null
  const { flagKey, orgName } = data
  return (
    <Tooltip title={`See the user overrides at ${orgName}`}>
      <RouteLink
        to="/internal/feature-flags/$flagKey/users"
        params={{ flagKey }}
        search={{ q: orgName }}
        variant="primary"
        size="base"
        className="tabular-nums"
      >
        {data.userOverrides}
      </RouteLink>
    </Tooltip>
  )
}

type Params = ValueGetterParams<CompanyFlagRow>

/** The status, setting, source and user-override columns of a company × flag grid. Each grid adds
 * its own identity columns. Untyped by row because `ColDef` is invariant in it: the getters type
 * their own params. */
export const COMPANY_FLAG_COLUMNS: ColDef[] = [
  {
    colId: "status",
    headerName: "Status",
    valueGetter: ({ data }: Params) => (data ? FLAG_STATE_ORDER[data.status.state] : undefined),
    cellRenderer: FlagStatusCell,
    cellClass: "ag-cell-vcenter",
    width: 100,
  },
  {
    colId: "setting",
    headerName: "Setting",
    headerTooltip: "Default follows the flag's default. On and Off override it.",
    cellRenderer: SettingCell,
    cellClass: "ag-cell-vcenter",
    suppressKeyboardEvent: keepSelectKeys,
    sortable: false,
    width: 150,
    minWidth: 150,
  },
  {
    colId: "source",
    headerName: "Source",
    valueGetter: ({ data }: Params) => data && flagSourceText(data.status).label,
    cellRenderer: SourceCell,
    minWidth: 130,
    flex: 1,
  },
  {
    colId: "userOverrides",
    headerName: "Users",
    headerTooltip:
      "User overrides: customer users with their own setting. Hakio staff aren't counted.",
    valueGetter: ({ data }: Params) => data?.userOverrides,
    cellRenderer: UserOverridesCell,
    width: 70,
  },
]
